// Orden de cotización: listado por bloques (general + mix) o plano; cross-sell (misma familia / mismo origen)
// Vista de bloques delegada a QuotationBlocksView (componente compartido con admin y vista aceptación).

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { useSourcingItems, useBrands, useOrigins, useOriginPorts, useMoqUnits, useLoadingMethods, useSourcingItemFamilies, useProductFamilyMixOptions } from '@/hooks/use-store';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { useToast } from '@/hooks/use-toast';
import { QuotationBlocksView } from '@/components/store/QuotationBlocksView';
import { QuotationAcceptanceView } from '@/components/store/QuotationAcceptanceView';
import { postWorkerApiWithAuth } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { Package, ShoppingCart } from 'lucide-react';
import type { RowLoadType } from '@/lib/store-types';
import { isLoadingMethodPalletized } from '@/lib/constants';

const QUERY_KEY_CART = ['quotation-request-cart'] as const;

const StoreCart = () => {
  const queryClient = useQueryClient();
  const { items, request, updateQuantity, remove, add, patchBlocks, isLoading, readOnly } = useQuotationCart();
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: originPorts } = useOriginPorts();
  const { data: moqUnits } = useMoqUnits();
  const { data: loadingMethods } = useLoadingMethods();
  const { data: allSourcingItems } = useSourcingItems(null);
  const { data: sourcingItemFamilies } = useSourcingItemFamilies();
  const { data: mixOptionsRows } = useProductFamilyMixOptions();
  const { toast } = useToast();
  const [respondPending, setRespondPending] = useState(false);

  const itemIdToFamilyId = useMemo(() => {
    if (!sourcingItemFamilies?.length) return undefined;
    const map: Record<string, string> = {};
    sourcingItemFamilies.forEach((r) => {
      if (map[r.item_id] == null) map[r.item_id] = r.family_id;
    });
    return map;
  }, [sourcingItemFamilies]);

  const totalItems = items.reduce((acc, i) => acc + i.quantity, 0);

  const handleRespond = useCallback(
    async (accepted: boolean) => {
      if (!request?.id) return;
      setRespondPending(true);
      const res = await postWorkerApiWithAuth(
        '/api/quotation-request/respond',
        { request_id: request.id, accepted },
        () => supabase.auth.getSession()
      );
      setRespondPending(false);
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY_CART });
        toast({ title: accepted ? 'Cotización aceptada' : 'Cotización rechazada' });
      } else {
        toast({ title: 'Error al responder la cotización', variant: 'destructive' });
      }
    },
    [request?.id, queryClient, toast]
  );

  /** Default load type per product from loading_method (centralized in constants). */
  const getDefaultLoadType = useCallback(
    (sourcingItemId: string): RowLoadType => {
      const item = allSourcingItems?.find((i) => i.id === sourcingItemId);
      const methodName = loadingMethods?.find((m) => m.id === item?.loading_method_id)?.name;
      return isLoadingMethodPalletized(methodName) ? 'pallets' : 'floor';
    },
    [allSourcingItems, loadingMethods]
  );

  /** Persist per-row load type for a mix block. */
  const updateMixRowLoadType = useCallback(
    (blockIndex: number, rowIndex: number, value: RowLoadType) => {
      if (!request?.blocks?.mix?.[blockIndex]) return;
      const mix = [...request.blocks.mix];
      const block = { ...mix[blockIndex], rows: [...(mix[blockIndex].rows ?? [])] };
      const row = block.rows[rowIndex];
      if (!row) return;
      block.rows[rowIndex] = { ...row, row_load_type: value };
      mix[blockIndex] = block;
      patchBlocks({ ...request.blocks, mix });
    },
    [request?.blocks, patchBlocks]
  );

  const clientProps = useMemo(
    () => ({
      patchBlocks,
      add,
      updateQuantity,
      remove,
      toast: (t: { title: string; description?: string; variant?: 'destructive' }) => toast(t),
      getDefaultLoadType,
      updateMixRowLoadType,
      allSourcingItems: allSourcingItems ?? [],
      loadingMethods: loadingMethods ?? [],
      brands: brands ?? [],
      origins: origins ?? [],
      originPorts: originPorts ?? [],
      moqUnits: moqUnits ?? [],
      itemIdToFamilyId,
      mixOptions: mixOptionsRows ?? undefined,
    }),
    [
      patchBlocks,
      add,
      updateQuantity,
      remove,
      toast,
      getDefaultLoadType,
      updateMixRowLoadType,
      allSourcingItems,
      loadingMethods,
      brands,
      origins,
      originPorts,
      moqUnits,
      itemIdToFamilyId,
      mixOptionsRows,
    ]
  );

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Orden' },
  ];

  const lockMessage =
    request?.status === 'en_revision'
      ? 'El equipo está preparando tu cotización; no puedes editar mientras tanto.'
      : request?.status === 'cotizado'
        ? 'Cotización enviada. Puedes revisar los precios y aceptar o rechazar.'
        : null;

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      
      {/* Header: título + cartel pedido mínimo a la derecha */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Orden de cotización</h1>
          {items.length > 0 && (
            <p className="text-sm text-zinc-600 mt-1">
              {items.length} {items.length === 1 ? 'producto' : 'productos'} · {totalItems.toLocaleString()} unidades
            </p>
          )}
        </div>
        <div className="shrink-0 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-2 text-sm font-medium text-zinc-700">
          Pedido mínimo 1 Contenedor lleno
        </div>
      </div>

      {/* Solo mostrar mensaje de bloqueo cuando se muestra la cotización (vista aceptación cotizado), no en la vista limpia (en_revision/pending). */}
      {readOnly && lockMessage && request?.status === 'cotizado' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {lockMessage}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-xl border border-zinc-200 bg-gradient-to-br from-zinc-50 to-white p-12 text-center">
          <p className="text-zinc-600">Cargando tu orden...</p>
        </div>
      ) : request?.status != null && request.status !== 'draft' ? (
        /* Cotización ya enviada o en otro estado: página limpia para empezar otra; solo se muestra el bloque para añadir. */
        <>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Tu solicitud fue enviada. Añade productos aquí para una nueva cotización.
          </div>
          <QuotationBlocksView
            request={null}
            items={[]}
            mode="client"
            readOnly={false}
            clientProps={clientProps}
            showOnlyAddBlock
          />
          <Button asChild size="lg" className="w-full sm:w-auto gap-2">
            <Link to="/store/cart/request">
              <ShoppingCart className="h-4 w-4" />
              Solicitar cotización
            </Link>
          </Button>
        </>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-zinc-200 bg-gradient-to-br from-zinc-50 to-white p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-zinc-100 flex items-center justify-center">
              <Package className="h-8 w-8 text-zinc-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 mb-1">Tu orden está vacía</h2>
              <p className="text-zinc-600">Explora nuestro catálogo y añade productos para solicitar cotización.</p>
            </div>
            <Button asChild size="lg">
              <Link to="/store">Explorar catálogo</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Columna principal: borradores / edición de la orden. La vista formal se ve en la página de seguimiento. */}
          <div className="flex-1 min-w-0 space-y-4">
            <QuotationBlocksView
              request={request}
              items={items}
              mode="client"
              readOnly={readOnly}
              clientProps={clientProps}
            />
            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto gap-2"
              disabled={request?.status === 'cotizado' || request?.status === 'finished'}
            >
              <Link to="/store/cart/request">
                <ShoppingCart className="h-4 w-4" />
                Solicitar cotización
              </Link>
            </Button>
            {request?.status === 'cotizado' && (
              <p className="text-xs text-zinc-500">
                Esta orden ya está cotizada. Revisa y acepta la cotización desde{' '}
                <Link to="/store/quotations" className="underline">
                  tu historial de cotizaciones
                </Link>
                .
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreCart;
